import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { getExportFileName } from '../../utils/fileExportNaming';
import React, { useState, useEffect, useRef } from 'react';
import { Search, Filter, Download, MoreVertical } from 'lucide-react';
import { getAllClients, toggleClientStatus, getAllSlabs } from '../../api/createClientApi';
import { getAllVehicleClientAssignments } from '../../api/vehicalsManagementApi';
import { getAllRoutes } from '../../api/routesApi';
import AddClientModal from './AddClientModal';
import EditClientModal from './EditClientModal';
import ClientDetail from './ClientDetail';
import CustomSelect from '../shared/CustomSelect';
import { toast } from '../shared/Toast';
import { showConfirm } from '../shared/ConfirmContext';

const StatCard = ({ value, label, isActive, onClick }) => (
  <div
    onClick={onClick}
    style={{
      backgroundColor: isActive ? '#E8F5EE' : '#FFFFFF',
      borderRadius: '8px',
      boxShadow: '0 4px 12px rgba(0,0,0,0.03)',
      flex: 1,
      padding: '24px 16px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      border: isActive ? '2px solid #07A95B' : '2px solid transparent',
      transition: 'all 0.2s ease'
    }}
    onMouseEnter={(e) => {
      if (!isActive) {
        e.currentTarget.style.backgroundColor = '#F5FAF7';
        e.currentTarget.style.border = '2px solid #D1E7DD';
      }
    }}
    onMouseLeave={(e) => {
      if (!isActive) {
        e.currentTarget.style.backgroundColor = '#FFFFFF';
        e.currentTarget.style.border = '2px solid transparent';
      }
    }}
  >
    <div style={{ fontSize: '56px', fontWeight: 700, color: '#07A95B', lineHeight: 1.1 }}>{value}</div>
    <div style={{ fontSize: '18px', fontWeight: 600, color: '#333333', marginTop: '4px' }}>{label}</div>
  </div>
);

const FilterDropdown = ({ isOpen, onClose, sortOrder, onSortChange, onApply }) => {
  if (!isOpen) return null;

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 100, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', paddingRight: '24px' }}>
      <div onClick={onClose} style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', animation: 'fadeIn 0.2s ease-out' }} />
      <div style={{
        position: 'relative',
        width: '320px',
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
        padding: '24px',
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
        border: 'none',
        zIndex: 101,
        animation: 'slideInRight 0.3s cubic-bezier(0.16, 1, 0.3, 1)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#07A95B' }}>Sort By</h3>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <label style={{ fontSize: '14px', fontWeight: 500, color: '#333' }}>Company Name</label>
            <CustomSelect
              value={sortOrder}
              onChange={(val) => onSortChange(val)}
              options={[
                { value: 'az', label: 'A - Z' },
                { value: 'za', label: 'Z - A' },
              ]}
              width="100%"
            />
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '4px' }}>
          <button
            onClick={() => { onApply(); onClose(); }}
            style={{
              backgroundColor: '#20B967',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              padding: '12px',
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer',
              width: '100%',
              transition: 'background-color 0.2s',
            }}
            onMouseEnter={e => e.currentTarget.style.backgroundColor = '#1c9d57'}
            onMouseLeave={e => e.currentTarget.style.backgroundColor = '#20B967'}
          >
            Apply
          </button>
          <button
            onClick={onClose}
            style={{
              backgroundColor: 'white',
              color: '#20B967',
              border: '1px solid #20B967',
              borderRadius: '6px',
              padding: '12px',
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer',
              width: '100%',
              transition: 'background-color 0.2s',
            }}
            onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f0fcf5'}
            onMouseLeave={e => e.currentTarget.style.backgroundColor = 'white'}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

const ActionBar = ({ clientsData, searchValue, onSearchChange, sortOrder, onSortChange, onApplySort }) => {
  const [hoverFilter, setHoverFilter] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [hoverDownload, setHoverDownload] = useState(false);
  const [isDownloadOpen, setIsDownloadOpen] = useState(false);
  const downloadButtonRef = useRef(null);
  const [downloadMenuPosition, setDownloadMenuPosition] = useState({ top: 0, right: 0 });

  useEffect(() => {
    if (isDownloadOpen && downloadButtonRef.current) {
      const rect = downloadButtonRef.current.getBoundingClientRect();
      setDownloadMenuPosition({
        top: rect.bottom + window.scrollY + 4,
        right: window.innerWidth - rect.right
      });
    }
  }, [isDownloadOpen]);

  const downloadCSV = () => {
    if (!clientsData || clientsData.length === 0) {
      toast.warning('No data to download');
      return;
    }
    const headers = ['Company Name', 'Assigned Vehicles', 'No of Slabs', 'No of Routes'];
    const rows = clientsData.map(c => [c.companyName, c.vehicles, c.slabs, c.routesCount || 0]);
    const csvContent = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = getExportFileName({ reportName: 'Clients', data: clientsData, ext: 'csv' });
    a.click();
    URL.revokeObjectURL(url);
    setIsDownloadOpen(false);
  };

  const downloadExcel = () => {
    if (!clientsData || clientsData.length === 0) {
      toast.warning('No data to download');
      return;
    }
    const headers = ['Company Name', 'Assigned Vehicles', 'No of Slabs', 'No of Routes'];
    const rows = clientsData.map(c => [c.companyName, c.vehicles, c.slabs, c.routesCount || 0]);

    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Clients');
    const fileName = getExportFileName({ reportName: 'Clients', data: clientsData, ext: 'xlsx' });
    XLSX.writeFile(wb, fileName);
    setIsDownloadOpen(false);
  };

  const downloadPDF = () => {
    if (!clientsData || clientsData.length === 0) {
      toast.warning('No data to download');
      return;
    }
    const doc = new jsPDF();
    doc.text('Clients List', 14, 15);

    const headers = [['Company Name', 'Assigned Vehicles', 'No of Slabs', 'No of Routes']];
    const rows = clientsData.map(c => [c.companyName, c.vehicles, c.slabs, c.routesCount || 0]);

    autoTable(doc, {
      head: headers,
      body: rows,
      startY: 25,
      styles: { fontSize: 10, cellPadding: 3 },
      headStyles: { fillColor: [32, 185, 103], textColor: 255 }
    });

    doc.save('clients.pdf');
    setIsDownloadOpen(false);
  };

  return (
    <div style={{ backgroundColor: '#EEF8F3', borderRadius: '8px', padding: '12px 20px', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '16px', marginTop: '16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', backgroundColor: '#F5F1FA', borderRadius: '20px', padding: '10px 16px', width: '280px' }}>
        <input type="text" placeholder="Search company name" value={searchValue} onChange={(e) => onSearchChange(e.target.value)} style={{ border: 'none', background: 'transparent', outline: 'none', flexGrow: 1, fontFamily: 'inherit', color: '#333', fontSize: '14px' }} />
        <Search size={18} color="#555" />
      </div>
      <div style={{ position: 'relative' }}>
        <button
          onClick={() => setIsFilterOpen(!isFilterOpen)}
          onMouseEnter={() => setHoverFilter(true)} onMouseLeave={() => setHoverFilter(false)}
          style={{ backgroundColor: hoverFilter ? '#1c9d57' : '#20B967', color: 'white', border: 'none', borderRadius: '6px', padding: '10px 16px', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: 500, transition: 'background-color 0.2s' }}
        >
          Filters <Filter size={16} />
        </button>
        <FilterDropdown
          isOpen={isFilterOpen}
          onClose={() => setIsFilterOpen(false)}
          sortOrder={sortOrder}
          onSortChange={onSortChange}
          onApply={onApplySort}
        />
      </div>
      <div style={{ position: 'relative' }} ref={downloadButtonRef}>
        <button
          onClick={() => setIsDownloadOpen(!isDownloadOpen)}
          onMouseEnter={() => setHoverDownload(true)} onMouseLeave={() => setHoverDownload(false)}
          style={{ backgroundColor: hoverDownload ? '#f0fcf5' : 'white', color: '#20B967', border: '1px solid #20B967', borderRadius: '6px', padding: '10px 16px', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: 500, transition: 'background-color 0.2s' }}
        >
          Download <Download size={16} />
        </button>
        {isDownloadOpen && (
          <>
            <div onClick={() => setIsDownloadOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 100 }} />
            <div style={{
              position: 'fixed',
              top: downloadMenuPosition.top,
              right: downloadMenuPosition.right,
              backgroundColor: 'white',
              borderRadius: '8px',
              boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
              zIndex: 101,
              minWidth: '140px',
              overflow: 'hidden',
              border: '1px solid #E5E7EB'
            }}>
              <div onClick={downloadCSV} style={{ padding: '11px 16px', fontSize: '14px', color: '#333', cursor: 'pointer', transition: 'background-color 0.15s' }}
                onMouseEnter={e => e.currentTarget.style.backgroundColor = '#F9FAFB'}
                onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                CSV
              </div>
              <div onClick={downloadExcel} style={{ padding: '11px 16px', fontSize: '14px', color: '#333', cursor: 'pointer', transition: 'background-color 0.15s' }}
                onMouseEnter={e => e.currentTarget.style.backgroundColor = '#F9FAFB'}
                onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                Excel
              </div>
              <div onClick={downloadPDF} style={{ padding: '11px 16px', fontSize: '14px', color: '#333', cursor: 'pointer', transition: 'background-color 0.15s' }}
                onMouseEnter={e => e.currentTarget.style.backgroundColor = '#F9FAFB'}
                onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                PDF
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const ActionsMenu = ({ onViewDetails, onEdit, onToggleStatus, isActive }) => {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, right: 0 });
  const [positionAbove, setPositionAbove] = useState(false);

  useEffect(() => {
    if (open && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const dropdownHeight = 140;
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;

      const shouldPositionAbove = spaceBelow < dropdownHeight && spaceAbove > spaceBelow;
      setPositionAbove(shouldPositionAbove);

      setMenuPosition({
        top: shouldPositionAbove
          ? rect.top + window.scrollY - dropdownHeight
          : rect.bottom + window.scrollY,
        right: window.innerWidth - rect.right
      });
    }
  }, [open]);

  return (
    <div style={{ position: 'relative' }} ref={buttonRef}>
      <div onClick={() => setOpen(!open)} style={{ width: '80px', display: 'flex', justifyContent: 'center', cursor: 'pointer' }}>
        <MoreVertical size={20} color="#555" />
      </div>
      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 100 }} />
          <div style={{
            position: 'fixed',
            top: menuPosition.top + (positionAbove ? 0 : 4),
            right: menuPosition.right,
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
            zIndex: 101,
            minWidth: '160px',
            overflow: 'hidden',
            border: '1px solid #E5E7EB'
          }}>
            {[
              { label: 'View Details', action: onViewDetails, color: '#333' },
              { label: 'Edit', action: onEdit, color: '#333' },
              { label: isActive ? 'Deactivate' : 'Activate', action: onToggleStatus, color: isActive ? '#F04438' : '#20B967' },
            ].map((item, i) => (
              <div key={i} onClick={() => { item.action && item.action(); setOpen(false); }} style={{ padding: '11px 16px', fontSize: '14px', color: item.color, cursor: 'pointer', transition: 'background-color 0.15s' }}
                onMouseEnter={e => e.currentTarget.style.backgroundColor = '#F9FAFB'}
                onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                {item.label}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const TableHeader = () => (
  <div style={{ display: 'flex', backgroundColor: '#212A30', color: 'white', padding: '12px 16px', fontSize: '13px', fontWeight: 500 }}>
    <div style={{ width: '50px', display: 'flex', alignItems: 'center' }}><input type="checkbox" style={{ width: '16px', height: '16px', cursor: 'pointer' }} /></div>
    <div style={{ flex: 2 }}>Company Name</div>
    <div style={{ flex: 1, textAlign: 'center' }}>Assigned Vehicles</div>
    <div style={{ flex: 1, textAlign: 'center' }}>No of Slabs</div>
    <div style={{ flex: 1.5, textAlign: 'center' }}>No of Routes</div>
    <div style={{ width: '80px', textAlign: 'center' }}>Actions</div>
  </div>
);

const TableRow = ({ data, onViewDetails, onEdit, onToggleStatus }) => (
  <div style={{ display: 'flex', padding: '12px 16px', borderBottom: '1px solid #F0F0F0', alignItems: 'center', fontSize: '14px', color: '#333', backgroundColor: data.isActive === false ? '#FEF2F2' : 'white' }}
    onMouseEnter={e => e.currentTarget.style.backgroundColor = data.isActive === false ? '#FEE2E2' : '#FAFAFA'}
    onMouseLeave={e => e.currentTarget.style.backgroundColor = data.isActive === false ? '#FEF2F2' : 'white'}
  >
    <div style={{ width: '50px', display: 'flex', alignItems: 'center' }}><input type="checkbox" style={{ width: '16px', height: '16px', cursor: 'pointer' }} /></div>
    <div style={{ flex: 2, fontWeight: 500, cursor: 'pointer', color: data.isActive === false ? '#9CA3AF' : '#333', textDecoration: data.isActive === false ? 'line-through' : 'none' }} onClick={onViewDetails}>{data.companyName}</div>
    <div style={{ flex: 1, textAlign: 'center', color: data.isActive === false ? '#9CA3AF' : '#333' }}>{data.vehicles}</div>
    <div style={{ flex: 1, textAlign: 'center', color: data.isActive === false ? '#9CA3AF' : '#333' }}>{data.slabs || 0}</div>
    <div style={{ flex: 1.5, textAlign: 'center', color: data.isActive === false ? '#9CA3AF' : '#555', fontSize: '14px' }}>{data.routesCount || 0}</div>
    <ActionsMenu onViewDetails={onViewDetails} onEdit={onEdit} onToggleStatus={onToggleStatus} isActive={data.isActive !== false} />
  </div>
);

const Clients = ({ onAddClient }) => {
  const [viewingDetail, setViewingDetail] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingClientId, setEditingClientId] = useState(null);
  const [clientsData, setClientsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 1 });

  const [allClients, setAllClients] = useState([]);
  const [sortOrder, setSortOrder] = useState('az');
  const [appliedSortOrder, setAppliedSortOrder] = useState('az');
  const [statusFilter, setStatusFilter] = useState('active'); // 'all', 'active', 'inactive'

  // Fetch clients from API
  useEffect(() => {
    fetchClients(currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  // Listen for sheet upload events to refresh data
  useEffect(() => {
    const handleSheetUploaded = () => {
      fetchClients(currentPage);
      fetchAllClientsForStats();
    };
    window.addEventListener('clientSheetUploaded', handleSheetUploaded);
    return () => window.removeEventListener('clientSheetUploaded', handleSheetUploaded);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  // Fetch all vehicle assignments across all pages
  const fetchAllVehicleAssignments = async () => {
    try {
      // First fetch to get pagination info
      const firstResponse = await getAllVehicleClientAssignments(1, 100);
      const totalCount = firstResponse.totalCount || firstResponse.pagination?.total || 0;
      const assignments = firstResponse.assignments || firstResponse.data || [];

      if (totalCount <= 100) {
        return assignments;
      }

      // Calculate total pages
      const totalPages = Math.ceil(totalCount / 100);
      const allAssignments = [...assignments];

      // Fetch remaining pages
      for (let page = 2; page <= totalPages; page++) {
        const response = await getAllVehicleClientAssignments(page, 100);
        const pageAssignments = response.assignments || response.data || [];
        allAssignments.push(...pageAssignments);
      }

      return allAssignments;
    } catch (err) {
      return [];
    }
  };

  const fetchClients = async (page = 1) => {
    try {
      setLoading(true);
      // Fetch clients, slabs, and routes in parallel
      // Fetch all vehicle assignments separately (handles pagination)
      const [clientsResponse, slabsResponse, routesResponse, allAssignments] = await Promise.all([
        getAllClients(page, itemsPerPage),
        getAllSlabs(),
        getAllRoutes(),
        fetchAllVehicleAssignments()
      ]);

      // Handle slabs data - API returns {clientStores: [...]}
      const slabsArray = slabsResponse.clientStores || slabsResponse.slabs || slabsResponse.data || [];

      // Create a map of clientId to slab count
      const slabCountMap = slabsArray.reduce((acc, slab) => {
        const clientId = slab.clientId || slab.client_id;
        if (clientId) {
          acc[String(clientId)] = (acc[String(clientId)] || 0) + 1;
        }
        return acc;
      }, {});

      // Count vehicle assignments per client
      const vehicleCountMap = allAssignments.reduce((acc, assignment) => {
        const clientId = assignment.clientId || assignment.client_id;
        if (clientId) {
          // Use string keys for consistent matching
          acc[String(clientId)] = (acc[String(clientId)] || 0) + 1;
        }
        return acc;
      }, {});

      // Count routes per client
      const routesArray = routesResponse.routes || routesResponse.data || [];
      const routeCountMap = routesArray.reduce((acc, route) => {
        const clientId = route.clientId || route.client_id;
        if (clientId) {
          acc[String(clientId)] = (acc[String(clientId)] || 0) + 1;
        }
        return acc;
      }, {});

      // Handle different response formats
      const clientsArray = clientsResponse.clients || clientsResponse.data || [];
      const paginationData = clientsResponse.pagination || { total: clientsArray.length, totalPages: 1 };

      setPagination(paginationData);

      // DEBUG: Log first client and assignment count
      if (clientsArray.length > 0) {
      }

      if (clientsArray.length > 0) {
        // Map API fields to component expected fields
        const mappedClients = clientsArray.map(c => ({
          id: c.id,
          companyName: c.clientName || c.client_name || c.company_name || c.companyName || c.name || 'Unknown',
          clientName: c.clientName || c.client_name || c.company_name || c.companyName || c.name || 'Unknown',
          contactPerson: c.contactPerson || c.contact_person || c.contact || '',
          contactNumber: c.contactNumber || c.contact_number || c.phone || '',
          email: c.email || '',
          address: c.address || '',
          location: c.location || '',
          vehicles: vehicleCountMap[String(c.id)] || c.vehicles || c.assigned_vehicles || c.vehicle_count || 0,
          slabs: slabCountMap[String(c.id)] || 0,
          routesCount: routeCountMap[String(c.id)] || 0,
          isActive: c.isActive !== false // default to true if not specified
        }));
        setClientsData(mappedClients);
      } else {
        setClientsData([]);
      }
    } catch (err) {
      setError('Failed to load clients');
      // Check if it's 401
      if (err.response?.status === 401) {
        setError('Session expired. Please login again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Fetch all clients for accurate stats (without pagination)
  const fetchAllClientsForStats = async () => {
    try {
      const response = await getAllClients(1, 1000);
      const clientsArray = response.clients || response.data || [];

      // Map all clients with isActive status
      const mappedAllClients = clientsArray.map(c => ({
        id: c.id,
        isActive: c.isActive !== false
      }));

      setAllClients(mappedAllClients);
    } catch (err) {
    }
  };

  useEffect(() => {
    fetchClients(currentPage);
    fetchAllClientsForStats(); // Fetch all for stats
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  useEffect(() => {
    const handleClientAdded = () => {
      fetchClients(currentPage);
      fetchAllClientsForStats();
    };
    window.addEventListener('clientAdded', handleClientAdded);
    return () => {
      window.removeEventListener('clientAdded', handleClientAdded);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  // Filter and sort clients based on search term, status filter, and sort order
  const filteredClients = clientsData.filter(c => {
    const matchesSearch = searchTerm
      ? c.companyName?.toLowerCase().includes(searchTerm.toLowerCase())
      : true;
    const matchesStatus = statusFilter === 'all'
      ? true
      : statusFilter === 'active'
        ? c.isActive !== false
        : c.isActive === false;
    return matchesSearch && matchesStatus;
  });

  // Apply sorting
  const sortedClients = [...filteredClients].sort((a, b) => {
    const nameA = a.companyName?.toLowerCase() || '';
    const nameB = b.companyName?.toLowerCase() || '';
    if (appliedSortOrder === 'az') {
      return nameA.localeCompare(nameB);
    } else {
      return nameB.localeCompare(nameA);
    }
  });

  const handleToggleStatus = async (id, currentStatus) => {
    const isDeactivating = currentStatus !== false;
    const action = isDeactivating ? 'deactivate' : 'activate';
    const confirmed = await showConfirm({
      title: isDeactivating ? 'Deactivate Client' : 'Activate Client',
      message: `Are you sure you want to ${action} this client?`,
      danger: isDeactivating,
      type: isDeactivating ? 'danger' : 'success',
      confirmLabel: isDeactivating ? 'Deactivate' : 'Activate',
      cancelLabel: 'Cancel',
    });
    if (!confirmed) return;

    try {
      await toggleClientStatus(id);
      await fetchClients(currentPage); // Refresh list
      await fetchAllClientsForStats(); // Refresh stats
      toast.success(`Client ${isDeactivating ? 'deactivated' : 'activated'} successfully!`);
    } catch (err) {
      toast.error(err.response?.data?.message || `Failed to ${action} client`);
    }
  };

  // Calculate active and inactive counts from all clients
  const activeCount = allClients.filter(c => c.isActive !== false).length;
  const inactiveCount = allClients.filter(c => c.isActive === false).length;

  if (viewingDetail) {
    return <ClientDetail client={viewingDetail} onBack={() => setViewingDetail(null)} />;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', gap: '16px' }}>
        <StatCard
          value={pagination.total.toString().padStart(2, '0')}
          label="Total Clients"
          isActive={statusFilter === 'all'}
          onClick={() => setStatusFilter('all')}
        />
        <StatCard
          value={activeCount.toString().padStart(2, '0')}
          label="Active Clients"
          isActive={statusFilter === 'active'}
          onClick={() => setStatusFilter('active')}
        />
        <StatCard
          value={inactiveCount.toString().padStart(2, '0')}
          label="Inactive Clients"
          isActive={statusFilter === 'inactive'}
          onClick={() => setStatusFilter('inactive')}
        />
      </div>
      <ActionBar
        clientsData={clientsData}
        searchValue={searchTerm}
        onSearchChange={setSearchTerm}
        sortOrder={sortOrder}
        onSortChange={setSortOrder}
        onApplySort={() => setAppliedSortOrder(sortOrder)}
      />
      {error && (
        <div style={{ backgroundColor: '#FCE8E8', color: '#F04438', padding: '12px 16px', borderRadius: '6px', marginTop: '12px', fontSize: '14px' }}>
          {error} (Check browser console for details)
        </div>
      )}
      {loading && (
        <div style={{ textAlign: 'center', padding: '32px', color: '#666', marginTop: '12px' }}>
          Loading clients...
        </div>
      )}
      <div style={{ marginTop: '16px', backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.03)', overflow: 'hidden' }}>
        <TableHeader />
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {!loading && filteredClients.length === 0 ? (
            <div style={{ padding: '32px', textAlign: 'center', color: '#888', fontSize: '14px' }}>
              {searchTerm ? 'No clients found matching your search.' : 'No clients found. Click the + button to add a client.'}
            </div>
          ) : (
            sortedClients.map((client, index) => (
              <TableRow
                key={index}
                data={client}
                onViewDetails={() => setViewingDetail(client)}
                onEdit={() => {
                  setEditingClientId(client.id);
                  setEditModalOpen(true);
                }}
                onToggleStatus={() => handleToggleStatus(client.id, client.isActive)}
              />
            ))
          )}
        </div>
        {/* Pagination */}
        {!loading && clientsData.length > 0 && pagination.total > 10 && (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '16px', borderTop: '1px solid #E5E7EB' }}>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                style={{
                  padding: '8px 12px',
                  borderRadius: '6px',
                  border: '1px solid #D1D5DB',
                  backgroundColor: currentPage === 1 ? '#F3F4F6' : 'white',
                  color: currentPage === 1 ? '#9CA3AF' : '#374151',
                  cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                  fontSize: '14px'
                }}
              >
                Previous
              </button>
              {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map(page => (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  style={{
                    padding: '8px 12px',
                    borderRadius: '6px',
                    border: '1px solid #D1D5DB',
                    backgroundColor: currentPage === page ? '#20B967' : 'white',
                    color: currentPage === page ? 'white' : '#374151',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  {page}
                </button>
              ))}
              <button
                onClick={() => setCurrentPage(prev => Math.min(pagination.totalPages, prev + 1))}
                disabled={currentPage === pagination.totalPages}
                style={{
                  padding: '8px 12px',
                  borderRadius: '6px',
                  border: '1px solid #D1D5DB',
                  backgroundColor: currentPage === pagination.totalPages ? '#F3F4F6' : 'white',
                  color: currentPage === pagination.totalPages ? '#9CA3AF' : '#374151',
                  cursor: currentPage === pagination.totalPages ? 'not-allowed' : 'pointer',
                  fontSize: '14px'
                }}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
      <AddClientModal
        isOpen={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        onSave={async () => {
          await fetchClients(currentPage); // Just refresh, onClose already closed modal
          await fetchAllClientsForStats(); // Refresh stats too
        }}
      />
      <EditClientModal
        isOpen={editModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          setEditingClientId(null);
        }}
        clientId={editingClientId}
        onSave={async () => {
          await fetchClients(currentPage);
          await fetchAllClientsForStats();
        }}
      />
    </div>
  );
};

export default Clients;
